import React, { useCallback, useRef, useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ErrorToast } from "../components/toastify";
import Swal from "sweetalert2";
import { AxiosGetMethod, AxiosPostMethod } from "../helpers/customAxios";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { setActivePage } from "../store/activePage";
import HandleLogout from "../helpers/logout";
import formatCustomDateWithTime from "../helpers/customDateFormatWithTime";
import { useLocation } from "react-router-dom";
import { FaCalendarAlt, FaMapMarkerAlt, FaSearch, FaUsers, FaQrcode, FaUser } from "react-icons/fa";
import CitySearchSelect from "../components/city-search-select";
import TalukSearchSelect from "../components/taluk-search-select";
import VillageAutocomplete from "../components/VillageAutocomplete";
import axios from "axios";
import { translateToTamil, CITY_TAMIL_DICTIONARY } from "../helpers/citySearchHelper";

const TRANSLATE_API_URL = "https://translation.digitalmoitech.com/translate";
const tamilTextRegex = /[\u0B80-\u0BFF]/;
const tamilRequiredMessage = "Please enter this field in Tamil";

const backendBaseUrl = (process.env.REACT_APP_API_URL || "")
  .trim()
  .replace(/\/+$/, "");

const resolveInvitationUrl = (eventDetails) => {
  let invitationPath =
    eventDetails?.invitation_file ||
    (eventDetails?.invitation && eventDetails?.event_id
      ? `/app/media/events/${eventDetails.event_id}/${eventDetails.invitation}`
      : null);

  if (!invitationPath) return null;
  if (/^https?:\/\//i.test(invitationPath)) return invitationPath;

  // Serve the file through the public backend route so Apache/Nginx static
  // rewrites cannot prevent the QR token page from displaying it.
  if (eventDetails?.event_id) {
    return `${backendBaseUrl}/customer/event-invitation?id=${encodeURIComponent(
      eventDetails.event_id,
    )}`;
  }

  // Older records may contain only the stored filename instead of the
  // public media path returned by the newer event-info query.
  if (!invitationPath.includes("/") && eventDetails?.event_id) {
    invitationPath = `/app/media/events/${eventDetails.event_id}/${invitationPath}`;
  }

  const normalizedPath = invitationPath.startsWith("/")
    ? invitationPath
    : `/${invitationPath}`;

  // The API base is sometimes configured as /app while the database path
  // already starts with /app/media. Avoid producing /app/app/media URLs.
  const normalizedBaseUrl =
    normalizedPath.startsWith("/app/") && /\/app$/i.test(backendBaseUrl)
      ? backendBaseUrl.slice(0, -4)
      : backendBaseUrl;

  return `${normalizedBaseUrl}${normalizedPath}`;
};

const resolveEventLocation = (eventDetails) => {
  if (!eventDetails) return "—";

  const locationParts = [
    eventDetails?.event_address,
    eventDetails?.event_city || eventDetails?.city,
    eventDetails?.event_state || eventDetails?.state,
  ].filter(Boolean);

  return locationParts.length ? locationParts.join(", ") : "—";
};

const normalizeText = (value) => String(value || "").toLowerCase().trim();

const getDateKey = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const isUpcomingEventDate = (value) => {
  const eventDateKey = getDateKey(value);
  if (!eventDateKey) return false;

  return eventDateKey >= getDateKey(new Date());
};

const getEventSearchBlob = (eventDetails) =>
  [
    eventDetails?.event_id,
    eventDetails?.event_type,
    eventDetails?.organisation_name,
    eventDetails?.event_city,
    eventDetails?.event_state,
    eventDetails?.event_address,
    eventDetails?.eventor_1,
    eventDetails?.eventor_2,
  ]
    .map(normalizeText)
    .join(" ");

const sortEventsByClosestDate = (events = []) =>
  [...events].sort((firstEvent, secondEvent) => {
    const todayKey = getDateKey(new Date());
    const firstDate = new Date(firstEvent?.event_date || 0);
    const secondDate = new Date(secondEvent?.event_date || 0);
    const firstKey = getDateKey(firstDate);
    const secondKey = getDateKey(secondDate);
    const firstIsToday = firstKey === todayKey;
    const secondIsToday = secondKey === todayKey;

    if (firstIsToday && !secondIsToday) return -1;
    if (!firstIsToday && secondIsToday) return 1;

    const firstTime = new Date(firstEvent?.event_date || 0).getTime();
    const secondTime = new Date(secondEvent?.event_date || 0).getTime();
    return firstTime - secondTime;
  });

const mergeEventsById = (...eventGroups) => {
  const eventsById = new Map();

  eventGroups.flat().forEach((eventItem) => {
    if (!eventItem?.event_id) return;
    eventsById.set(String(eventItem.event_id), eventItem);
  });

  return sortEventsByClosestDate(Array.from(eventsById.values()));
};

function getCombinedName(name, initial) {
  const cleanName = name ? String(name).trim() : "";
  const cleanInitial = initial ? String(initial).trim() : "";
  if (!cleanInitial) return cleanName;
  if (!cleanName) return cleanInitial;
  if (
    cleanName.endsWith(" " + cleanInitial) ||
    cleanName.startsWith(cleanInitial + " ") ||
    cleanName === cleanInitial
  ) {
    return cleanName;
  }
  return `${cleanName} ${cleanInitial}`;
}

const getCustomerRegistrationPayload = (values) => ({
  first_name: getCombinedName(values.name, values.initial),
  last_name: values.familyName || "-",
  occupation: values.occupation || "-",
  mobile: values.mobile,
  email: values.email || `${values.mobile}@moinote.local`,
  password: values.mobile,
  plot_no: "-",
  address: [values.village, values.area, values.city].filter(Boolean).join(", ") || "-",
  city: values.city || values.village || values.area || "-",
  state: values.state,
  pincode: "000000",
});

const parseOnlinePaymentDetails = (value) => {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch (error) {
    return { raw_details: String(value) };
  }
};

const CreateMoiToken = () => {
  const { userLoggedIn } = useSelector((state) => state.userStatus);
  const logo = require("../assets/images/logo.jpeg");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialEventId = queryParams.get("event_id") || "";
  const isQuickTokenPath = location.pathname.includes("quick-token");
  const formikRef = useRef(null);
  const currentMobileInputRef = useRef("");

  const getCurrentDateTime = () => {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = Object.fromEntries(
      fmt.formatToParts(new Date()).map((p) => [p.type, p.value]),
    );
    const hour = parts.hour === "24" ? "00" : parts.hour;
    return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
  };

  const selectRef = useRef(null);
  const assetTypeRef = useRef(null);
  const villageRef = useRef(null);
  const cityRef = useRef(null);
  const areaRef = useRef(null);
  const initialRef = useRef(null);
  const nameRef = useRef(null);
  const occupationRef = useRef(null);
  const amountRef = useRef(null);
  const thingnameRef = useRef(null);
  const submitRef = useRef(null);
  const [eventInfoList, setEventInfoList] = useState([]);
  const [selectedEventDetails, setSelectedEventDetails] = useState(null);
  const [isInvitationAvailable, setIsInvitationAvailable] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingEventInfo, setIsFetchingEventInfo] = useState(false);
  const [isMyselfChecked, setIsMyselfChecked] = useState(false);
  const [isFetchingCustomerInfo, setIsFetchingCustomerInfo] = useState(false);
  const [existingCustomerMobile, setExistingCustomerMobile] = useState("");
  const lastCheckedMobileRef = useRef("");
  const mobileLookupRequestIdRef = useRef(0);
  // Tracks whether a city-to-Tamil translation is still in-flight so that
  // handleSearchEvents can skip the premature first trigger (fired before the
  // Tamil variant is available) and wait for the one that fires after
  // translation resolves.
  const cityTranslationPendingRef = useRef(false);
  const [eventSearchQuery, setEventSearchQuery] = useState("");
  const [debouncedEventSearchQuery, setDebouncedEventSearchQuery] = useState("");
  const [showAllEventResults, setShowAllEventResults] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  // Version of selectedCity translated into Tamil, when selectedCity is English.
  const [selectedCityTamil, setSelectedCityTamil] = useState("");
  // Version of selectedCity translated into English, when selectedCity is Tamil.
  const [selectedCityEnglish, setSelectedCityEnglish] = useState("");
  const [selectedTaluk, setSelectedTaluk] = useState("");
  const [eventDateFilter, setEventDateFilter] = useState("");
  const [eventListTab, setEventListTab] = useState("today");
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [, setIsLocationConfirmed] = useState(false);
  const [villageSuggestionEnabled, setVillageSuggestionEnabled] = useState(() => {
    const saved = localStorage.getItem("dwt-village-suggestion-enabled");
    return saved !== null ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem(
      "dwt-village-suggestion-enabled",
      JSON.stringify(villageSuggestionEnabled)
    );
  }, [villageSuggestionEnabled]);

  const [tokenStep, setTokenStep] = useState(initialEventId ? "token" : "city");
  const dispatch = useDispatch();
  const invitationPreviewUrl = resolveInvitationUrl(selectedEventDetails);
  const eventLocation = resolveEventLocation(selectedEventDetails);
  const paymentDetails = parseOnlinePaymentDetails(
    selectedEventDetails?.online_payment_details,
  );
  const accountDetails = paymentDetails?.account_details;
  const upiId = paymentDetails?.upi_id;
  const rawPaymentDetails = paymentDetails?.raw_details;
  const fieldClassName =
    "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-bold text-slate-700 placeholder:text-slate-400 shadow-sm outline-none transition focus:bg-white focus:border-amber-600 focus:ring-2 focus:ring-amber-500/20";
  const disabledFieldClassName =
    "w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-xs font-bold text-slate-400 shadow-sm outline-none";
  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuList: (base) => ({
      ...base,
      maxHeight: 260,
    }),
  };
  const validationSchema = Yup.object({
    assetType: Yup.string().required("Asset type is required"),
    amount: Yup.number()
      .transform((value, original) => original === "" ? null : value)
      .nullable()
      .when("assetType", {
        is: (v) => v === "cash" || v === "online",
        then: (s) =>
          s.required("Amount is required").positive("Amount must be positive"),
        otherwise: (s) => s.notRequired(),
      }),
    thingname: Yup.string()
      .nullable()
      .when("assetType", {
        is: (v) => v === "things" || v === "ornaments",
        then: (s) => s.required("Please enter the item received"),
        otherwise: (s) => s.notRequired(),
      }),
    datetime: Yup.date().required("Date and time are required"),
    name: Yup.string().required("Name is required"),
    initial: Yup.string(),
    area: Yup.string(),
    village: Yup.string().test(
      "village-or-city",
      "Enter at least village or city",
      function (value) {
        const { city, area } = this.parent;
        return !!(value?.trim() || city?.trim() || area?.trim());
      },
    ),
    city: Yup.string().test(
      "village-or-city",
      "Enter at least village or city",
      function (value) {
        const { village, area } = this.parent;
        return !!(value?.trim() || village?.trim() || area?.trim());
      },
    ),
    state: Yup.string().required("State is required"),
    mobile: Yup.string()
      .test("guest-mobile", "Mobile number is required for guest tokens", value => userLoggedIn || !!value)
      .test(
        "is-valid-mobile",
        "Must be 10 digits",
        (val) => !val || /^[0-9]{10}$/.test(val)
      ),
    email: Yup.string().email("Invalid email address"),
    event_id: Yup.string().required("Please select an event"),
    occupation: Yup.string(),
    familyName: Yup.string(),
  });

  const initialValues = {
    event_id: initialEventId,
    amount: "",
    assetType: "cash",
    thingname: "",
    datetime: getCurrentDateTime(),
    name: "",
    initial: "",
    occupation: "",
    village: "",
    area: "",
    city: "",
    state: "தமிழ்நாடு",
    mobile: "",
    email: "",
    familyName: "",
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    if (![values.city, values.village, values.area].some(value => value?.trim())) {
      ErrorToast("Please enter at least a village, area or city to continue");
      setSubmitting(false);
      return;
    }

    try {
      const customerReady = await ensureCustomerRegistered(values);

      if (!customerReady) {
        setSubmitting(false);
        return;
      }

      const body = {
        event_id: values.event_id,
        asset_type: values.assetType,
        amount: values.amount,
        asset: values.thingname,
        name: getCombinedName(values.name, values.initial),
        occupation: values.occupation,
        family_name: values.familyName,
        mobile: values.mobile,
        village: values.village,
        area: values.area,
        city: values.city,
        state: values.state,
      };

      const MakeEntryRes = await AxiosPostMethod(
        userLoggedIn ? "customer/create-token" : "customer/create-guest-token",
        body,
        !userLoggedIn,
      );

      if (MakeEntryRes?.status === 200) {
        const token = MakeEntryRes.data.data.token;
        Swal.fire({
          icon: "success",
          title: `Your token is ${token}`,
          text: "Token generated successfully!",
          confirmButtonColor: "#f59e0b",
        });
        resetForm();
        setIsMyselfChecked(false);
        setExistingCustomerMobile("");
        lastCheckedMobileRef.current = "";
        
        if (initialEventId) {
          setTokenStep("token");
        } else {
          setSelectedEventDetails(null);
          setSelectedCity("");
          setSelectedCityTamil("");
          setSelectedTaluk("");
          setEventDateFilter("");
          setEventListTab("today");
          setIsLocationConfirmed(false);
          setTokenStep("city");
          setEventSearchQuery("");
        }
      } else {
        ErrorToast(MakeEntryRes?.data?.message || "Something went wrong. Please contact admin.");
      }
    } catch (error) {
      ErrorToast("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const ensureCustomerRegistered = async (values) => {
    const mobile = String(values.mobile || "").trim();

    if (!mobile) {
      if (userLoggedIn) return true;
      ErrorToast("Mobile number is required for guest tokens.");
      return false;
    }

    if (!/^[0-9]{10}$/.test(mobile)) return false;

    if (userLoggedIn) {
      const existingCustomerRes = await AxiosGetMethod(
        `customer/get-customer-info?phone_number=${mobile}`,
      );

      if (existingCustomerRes?.status === 200) return true;
    }

    const registrationRes = await AxiosPostMethod(
      "customer/registration",
      getCustomerRegistrationPayload({ ...values, mobile }),
      true,
    );

    if (
      registrationRes?.status &&
      registrationRes.status !== 200 &&
      registrationRes.status !== 409
    ) {
      ErrorToast("Customer registration failed. Please check the details and try again.");
      return false;
    }

    if (registrationRes?.status === 409) {
      const conflictMessage =
        registrationRes?.data?.data?.error ||
        registrationRes?.data?.message ||
        registrationRes?.data?.error ||
        "";

      if (conflictMessage.toLowerCase().includes("email")) {
        ErrorToast("This email is already registered.");
        return false;
      }

      if (conflictMessage.toLowerCase().includes("mobile")) {
        return true;
      }

      ErrorToast(conflictMessage || "This contact is already registered.");
      return false;
    }

    if (registrationRes?.status !== 200) {
      ErrorToast("Customer registration failed. Please check the details and try again.");
      return false;
    }

    return true;
  };

  // Keep the selected city in English, but match location metadata from either
  // English or Tamil event records.
  const selectedCityVariants = [selectedCity]
    .concat(selectedCityTamil ? [selectedCityTamil] : [])
    .concat(selectedCityEnglish ? [selectedCityEnglish] : [])
    .map(normalizeText)
    .filter(Boolean);

  const selectedCityLocation = locationOptions.find((locationItem) => {
    const locationValues = [locationItem.city, locationItem.city_tamil]
      .filter(Boolean)
      .map(normalizeText);

    return selectedCityVariants.some((variant) =>
      locationValues.some((locationValue) => locationValue === variant),
    );
  });

  const talukOptions = selectedCityLocation?.taluks || [];

  // Some legacy events have a valid city but no usable taluk metadata. Do not
  // block token creation in that case; search all upcoming events for the city.
  useEffect(() => {
    if (
      !locationsLoading &&
      selectedCity &&
      tokenStep === "taluk" &&
      talukOptions.length === 0
    ) {
      setIsLocationConfirmed(true);
      setTokenStep("event");
    }
  }, [locationsLoading, selectedCity, talukOptions.length, tokenStep]);

  /**
   * Called when the user picks a city from the event filter dropdown. The
   * selected city stays English in the input, while the Tamil variant is only
   * used internally for event search.
   */
  const handleSearchEvents = useCallback(
    async (cityOverride) => {
      const activeCity = cityOverride !== undefined ? cityOverride : selectedCity;
      const normalizedSearch = String(debouncedEventSearchQuery || "").trim();

      if (!activeCity && !eventDateFilter && normalizedSearch.length < 2) {
        setEventInfoList([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        /**
         * Dual-search strategy: the user selects an English city ("Chennai"),
         * then we query both the English value and its Tamil equivalent
         * ("சென்னை") in parallel and merge the results.
         */
        const fetchByCity = async (cityVariant) => {
          const query = new URLSearchParams();
          if (normalizedSearch.length >= 2) query.set("search", normalizedSearch);
          if (cityVariant) query.set("city", cityVariant);
          if (selectedTaluk) query.set("taluk", selectedTaluk);
          if (eventDateFilter) query.set("date", eventDateFilter);

          const res = await AxiosGetMethod(
            `customer/event-filter?${query.toString()}`,
          );
          if (res.status === 200) return Array.isArray(res.data.data) ? res.data.data : [];
          if (res.status === 401) { HandleLogout(); return []; }
          return [];
        };

        let cityVariants = [null];
        if (activeCity) {
          const norm = activeCity.toLowerCase().trim();
          const dictTa = CITY_TAMIL_DICTIONARY?.[norm];
          const dictEn = Object.entries(CITY_TAMIL_DICTIONARY || {}).find(
            ([, ta]) => ta === activeCity.trim(),
          )?.[0];
          cityVariants = [
            ...new Set(
              [
                activeCity,
                dictTa,
                dictEn,
                selectedCityTamil,
                selectedCityEnglish,
              ].filter(Boolean),
            ),
          ];
        }

        const resultGroups = await Promise.all(cityVariants.map(fetchByCity));
        const merged = mergeEventsById(...resultGroups).filter((eventItem) =>
          isUpcomingEventDate(eventItem?.event_date),
        );
        setEventInfoList(sortEventsByClosestDate(merged));
      } catch (error) {
        ErrorToast("Failed to load events");
      } finally {
        setIsSearching(false);
      }
    },
    [
      debouncedEventSearchQuery,
      eventDateFilter,
      selectedCity,
      selectedCityTamil,
      selectedCityEnglish,
      selectedTaluk,
    ],
  );

  /**
   * Called when the user picks a city from the event filter dropdown. The
   * selected city stays English in the input, while the Tamil variant is only
   * used internally for event search.
   */
  const handleCityFilterChange = useCallback(
    (city) => {
      setSelectedCity(city);
      setSelectedCityTamil("");
      setSelectedCityEnglish("");
      setSelectedTaluk("");
      setIsLocationConfirmed(true);
      setShowAllEventResults(false);

      if (!city) {
        setTokenStep("city");
        setEventInfoList([]);
        return;
      }

      setTokenStep("event");

      const isTamilCity = tamilTextRegex.test(city);

      if (!isTamilCity) {
        const normalizedCity = city.toLowerCase().trim();
        const dictTamil = CITY_TAMIL_DICTIONARY?.[normalizedCity];
        if (dictTamil) {
          setSelectedCityTamil(dictTamil);
        } else {
          cityTranslationPendingRef.current = true;
          translateToTamil(city)
            .then((tamil) => {
              if (tamil && tamil !== city) setSelectedCityTamil(tamil);
            })
            .catch(() => {})
            .finally(() => {
              cityTranslationPendingRef.current = false;
            });
        }
      } else {
        const foundEn = Object.entries(CITY_TAMIL_DICTIONARY || {}).find(
          ([, ta]) => ta === city.trim(),
        )?.[0];
        if (foundEn) {
          setSelectedCityEnglish(foundEn.charAt(0).toUpperCase() + foundEn.slice(1));
        } else {
          cityTranslationPendingRef.current = true;
          axios
            .post(
              TRANSLATE_API_URL,
              { text: city, source: "ta", target: "en", mode: "transliterate" },
              { headers: { "Content-Type": "application/json" } },
            )
            .then((res) => {
              const translated =
                (Array.isArray(res.data.suggestions) && res.data.suggestions[0]) ||
                res.data.translated;
              if (!translated || translated === city) return;
              setSelectedCityEnglish(translated);
            })
            .catch(() => {})
            .finally(() => {
              cityTranslationPendingRef.current = false;
            });
        }
      }

      // Trigger search immediately so network API call goes out without delay
      handleSearchEvents(city);
    },
    [handleSearchEvents],
  );

  const handleLoadEventLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const cachedLocations = localStorage.getItem("customer-event-locations");
      if (cachedLocations) {
        setLocationOptions(JSON.parse(cachedLocations));
      }

      const locationsRes = await AxiosGetMethod("customer/event-locations");
      if (locationsRes.status === 200) {
        const locations = Array.isArray(locationsRes.data.data)
          ? locationsRes.data.data
          : [];
        setLocationOptions(locations);
        localStorage.setItem("customer-event-locations", JSON.stringify(locations));
      } else if (locationsRes.status === 401) {
        HandleLogout();
      }
    } catch (error) {
      ErrorToast("Failed to load city list");
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  const handleMyselfCheckbox = async (checked, setFieldValue) => {
    setIsMyselfChecked(checked);
    setExistingCustomerMobile("");
    lastCheckedMobileRef.current = "";
    mobileLookupRequestIdRef.current += 1;
    if (mobileLookupDebounceRef.current) {
      clearTimeout(mobileLookupDebounceRef.current);
      mobileLookupDebounceRef.current = null;
    }
    setIsFetchingCustomerInfo(false);
    if (checked) {
      setFieldValue("assetType", "cash");
      setFieldValue("thingname", "");
      setIsFetchingCustomerInfo(true);
      try {
        const phone =
          localStorage.getItem("customer-mobile") ||
          localStorage.getItem("phone_number") ||
          "";
        const endpoint = phone
          ? `customer/get-customer-info?phone_number=${phone}`
          : "customer/get-customer-info";
        const res = await AxiosGetMethod(endpoint);
        if (res.status === 200) {
          const d = res.data.data;
          if (d.mobile) localStorage.setItem("customer-mobile", d.mobile);
          if (d.email) localStorage.setItem("customer-email", d.email);
          currentMobileInputRef.current = d.mobile || "";
          setFieldValue("name", d.first_name || "");
          setFieldValue("initial", "");
          setFieldValue("area", d.plot_no === "-" ? "" : (d.plot_no || ""));
          setFieldValue("occupation", d.occupation || "");
          setFieldValue("familyName", d.last_name || "");
          setFieldValue("mobile", d.mobile || "");
          setFieldValue("email", d.email || "");
          setFieldValue("village", d.address || "");
          setFieldValue("city", d.city || "");
          setFieldValue("state", d.state || "");
        } else if (res.status === 401) {
          HandleLogout();
        } else {
          ErrorToast("Failed to fetch your info. Please try again.");
          setIsMyselfChecked(false);
        }
      } catch (error) {
        ErrorToast("Network error. Could not fetch your info.");
        setIsMyselfChecked(false);
      } finally {
        setIsFetchingCustomerInfo(false);
      }
    } else {
      setFieldValue("name", "");
      setFieldValue("initial", "");
      setFieldValue("area", "");
      setFieldValue("occupation", "");
      setFieldValue("familyName", "");
      currentMobileInputRef.current = "";
      setFieldValue("mobile", "");
      setFieldValue("email", "");
      setFieldValue("village", "");
      setFieldValue("city", "");
      setFieldValue("state", "தமிழ்நாடு");
    }
  };

  const handleGetEventInfo = async (id) => {
    setIsFetchingEventInfo(true);
    try {
      const eventInfoRes = await AxiosGetMethod(`customer/event-info?id=${id}`, !userLoggedIn);
      if (eventInfoRes.status === 200) {
        const eventData = eventInfoRes.data.data[0];
        if (!eventData || !isUpcomingEventDate(eventData?.event_date)) {
          setSelectedEventDetails(null);
          formikRef.current?.setFieldValue("event_id", "");
          ErrorToast("This event is not available for token creation.");
          return;
        }

        const resolvedEventId = eventData.event_id || id;
        const eventInfo = { ...eventData, event_id: resolvedEventId };

        setSelectedEventDetails(eventInfo);
        setEventInfoList((currentEvents) =>
          mergeEventsById(currentEvents, [eventInfo]),
        );
        formikRef.current?.setFieldValue("event_id", String(resolvedEventId));
        setEventSearchQuery(
          eventInfo.organisation_name ||
          eventInfo.event_type ||
          String(resolvedEventId),
        );
        const acceptOnlinePayments = eventInfo?.["accept_online_payments"];
        if (!acceptOnlinePayments && assetTypeRef.current) {
          const option = assetTypeRef.current.querySelector(
            'option[value="online"]',
          );
          if (option) option.disabled = true;
        }
      } else if (eventInfoRes.status === 401) {
        if (userLoggedIn) HandleLogout();
      } else {
        ErrorToast("Something went wrong. Please try again later.");
      }
    } catch (error) {
      ErrorToast("Failed to fetch event details");
    } finally {
      setIsFetchingEventInfo(false);
    }
  };

  const resetMyselfFields = useCallback((setFieldValue) => {
    setIsMyselfChecked(false);
    setExistingCustomerMobile("");
    lastCheckedMobileRef.current = "";
    mobileLookupRequestIdRef.current += 1;
    if (mobileLookupDebounceRef.current) {
      clearTimeout(mobileLookupDebounceRef.current);
      mobileLookupDebounceRef.current = null;
    }
    setIsFetchingCustomerInfo(false);
    setFieldValue("name", "");
    setFieldValue("initial", "");
    setFieldValue("area", "");
    setFieldValue("occupation", "");
    setFieldValue("familyName", "");
    currentMobileInputRef.current = "";
    setFieldValue("mobile", "");
    setFieldValue("email", "");
    setFieldValue("village", "");
    setFieldValue("city", "");
    setFieldValue("state", "தமிழ்நாடு");
  }, []);

  const fillCustomerInfoFields = useCallback((data, setFieldValue, mobileFallback = "") => {
    setFieldValue("name", data.first_name || "");
    setFieldValue("initial", "");
    setFieldValue("area", data.plot_no === "-" ? "" : (data.plot_no || ""));
    setFieldValue("occupation", data.occupation || "");
    setFieldValue("familyName", data.last_name || "");
    setFieldValue("mobile", data.mobile || mobileFallback);
    setFieldValue("email", data.email || "");
    setFieldValue("village", data.address || "");
    setFieldValue("city", data.city || "");
    setFieldValue("state", data.state || "");
  }, []);

  const clearCustomerInfoFields = useCallback((setFieldValue) => {
    setFieldValue("name", "");
    setFieldValue("initial", "");
    setFieldValue("area", "");
    setFieldValue("occupation", "");
    setFieldValue("familyName", "");
    setFieldValue("email", "");
    setFieldValue("village", "");
    setFieldValue("city", "");
    setFieldValue("state", "தமிழ்நாடு");
  }, []);

  const mobileLookupDebounceRef = useRef(null);

  useEffect(
    () => () => {
      if (mobileLookupDebounceRef.current) {
        clearTimeout(mobileLookupDebounceRef.current);
      }
    },
    [],
  );

  const handleMobileLookup = useCallback(
    async (mobile, setFieldValue, requestId) => {
      if (!/^[0-9]{10}$/.test(mobile) || isMyselfChecked) {
        if (requestId === mobileLookupRequestIdRef.current) {
          setIsFetchingCustomerInfo(false);
        }
        return;
      }
      if (mobile === lastCheckedMobileRef.current) {
        if (requestId === mobileLookupRequestIdRef.current) {
          setIsFetchingCustomerInfo(false);
        }
        return;
      }

      // Debounce: cancel any pending lookup
      if (mobileLookupDebounceRef.current) {
        clearTimeout(mobileLookupDebounceRef.current);
      }

      lastCheckedMobileRef.current = mobile;
      setIsFetchingCustomerInfo(true);

      mobileLookupDebounceRef.current = setTimeout(async () => {
        try {
          if (requestId !== mobileLookupRequestIdRef.current) return;
          const res = await AxiosGetMethod(
            `customer/get-customer-info?phone_number=${mobile}`,
          );

          // Stale check — user has already moved on to a different number.
          if (
            requestId !== mobileLookupRequestIdRef.current ||
            currentMobileInputRef.current !== mobile
          ) {
            return;
          }

          if (res?.status === 200) {
            const customerData = res?.data?.data;
            if (customerData && typeof customerData === "object") {
              fillCustomerInfoFields(customerData, setFieldValue, mobile);
            }
            setExistingCustomerMobile(mobile);
          } else if (res?.status === 401) {
            HandleLogout();
          } else {
            setExistingCustomerMobile("");
            clearCustomerInfoFields(setFieldValue);
          }
        } catch (error) {
          if (requestId === mobileLookupRequestIdRef.current) {
            setExistingCustomerMobile("");
            clearCustomerInfoFields(setFieldValue);
          }
        } finally {
          // Only clear loading state if this lookup is still the active one
          if (requestId === mobileLookupRequestIdRef.current) {
            setIsFetchingCustomerInfo(false);
          }
        }
      }, 400);
    },
    [clearCustomerInfoFields, fillCustomerInfoFields, isMyselfChecked],
  );

  const handleMobileChange = useCallback(
    (event, setFieldValue) => {
      const mobile = event.target.value.replace(/\D/g, "").slice(0, 10);
      currentMobileInputRef.current = mobile;
      setFieldValue("mobile", mobile);
      mobileLookupRequestIdRef.current += 1;

      if (existingCustomerMobile && mobile !== existingCustomerMobile) {
        setExistingCustomerMobile("");
        lastCheckedMobileRef.current = "";
        clearCustomerInfoFields(setFieldValue);
      }

      if (mobile.length < 10) {
        lastCheckedMobileRef.current = "";
        setIsFetchingCustomerInfo(false);
        if (mobileLookupDebounceRef.current) {
          clearTimeout(mobileLookupDebounceRef.current);
          mobileLookupDebounceRef.current = null;
        }
        return;
      }

      if (mobile.length === 10) {
        if (userLoggedIn) {
          handleMobileLookup(
            mobile,
            setFieldValue,
            mobileLookupRequestIdRef.current,
          );
        } else {
          setExistingCustomerMobile("");
        }
      }
    },
    [clearCustomerInfoFields, existingCustomerMobile, handleMobileLookup, userLoggedIn],
  );

  const handleSelectEvent = useCallback(
    (eventDetails, setFieldValue) => {
      if (!eventDetails?.event_id) {
        setFieldValue("event_id", "");
        setSelectedEventDetails(null);
        resetMyselfFields(setFieldValue);
        return;
      }

      if (!isUpcomingEventDate(eventDetails?.event_date)) {
        setFieldValue("event_id", "");
        setSelectedEventDetails(null);
        ErrorToast("This event is not available for token creation.");
        return;
      }

      setFieldValue("event_id", String(eventDetails.event_id));
      setSelectedEventDetails(eventDetails);
      setTokenStep("token");
      handleGetEventInfo(eventDetails.event_id);

      if (isMyselfChecked) {
        resetMyselfFields(setFieldValue);
      }
    },
    [isMyselfChecked, resetMyselfFields],
  );

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Create a Token",
        activeMenu: "createToken",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedEventSearchQuery(eventSearchQuery);
      setShowAllEventResults(false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [eventSearchQuery]);

  useEffect(() => {
    if (userLoggedIn) {
      handleSearchEvents();
    }
  }, [handleSearchEvents, userLoggedIn]);

  useEffect(() => {
    if (userLoggedIn) {
      handleLoadEventLocations();
    }
  }, [handleLoadEventLocations, userLoggedIn]);

  useEffect(() => {
    if (initialEventId) {
      setTokenStep("token");
      handleGetEventInfo(initialEventId);
    } else {
      setSelectedEventDetails(null);
      setEventSearchQuery("");
      setTokenStep("city");
    }
  }, [initialEventId]);

  useEffect(() => {
    setIsInvitationAvailable(Boolean(invitationPreviewUrl));
  }, [invitationPreviewUrl]);

  const mergedEventInfoList = mergeEventsById(
    eventInfoList,
    selectedEventDetails ? [selectedEventDetails] : [],
  ).filter((eventItem) => {
    if (!isUpcomingEventDate(eventItem?.event_date)) return false;
    const isClosed =
      eventItem?.result_file ||
      eventItem?.is_closed === 1 ||
      eventItem?.is_closed === true ||
      eventItem?.is_closed === "1";
    return !isClosed;
  });
  const filteredEventList = sortEventsByClosestDate(
    mergedEventInfoList.filter((eventItem) => {
      const searchText = normalizeText(eventSearchQuery);
      if (!searchText) return true;
      return getEventSearchBlob(eventItem).includes(searchText);
    }),
  );
  const todayKey = getDateKey(new Date());
  const todayEvents = filteredEventList.filter((eventItem) => {
    const eventDateKey = getDateKey(eventItem?.event_date);
    return eventDateKey === todayKey;
  });
  const shouldFallbackToAllEvents =
    eventListTab === "today" &&
    todayEvents.length === 0 &&
    filteredEventList.length > 0;
  const activeEventListTab = shouldFallbackToAllEvents ? "all" : eventListTab;
  const tabEventList =
    activeEventListTab === "today" ? todayEvents : filteredEventList;
  const visibleEventList = showAllEventResults
    ? tabEventList
    : tabEventList.slice(0, 8);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {!userLoggedIn && (
        <div className="mx-auto max-w-3xl mb-6">
          <div className="flex items-center gap-3 bg-slate-900 border border-slate-800 rounded-2xl p-4 text-white shadow-md">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm">
              <img src={logo} alt="Digital Moi Tech" className="h-8.5 w-8.5 rounded-lg object-cover" />
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-amber-500">Digital Moi Tech</p>
              <h1 className="text-base font-black text-white">Event Token Creator</h1>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(36px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div
        className={`mx-auto flex flex-col gap-8 ${selectedEventDetails && (isQuickTokenPath || tokenStep === "token")
            ? "max-w-6xl lg:flex-row"
            : "max-w-3xl"
          }`}
      >
        <div className="flex-1 overflow-visible rounded-2xl border border-slate-100 bg-white shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
          <div className="rounded-t-2xl border-b border-slate-100 bg-slate-50/50 px-6 py-5">
            <h2 className="text-lg font-bold text-slate-800">
              Create Moi Token
            </h2>
            <p className="mt-1 text-xs font-medium text-slate-500">
              {isQuickTokenPath
                ? "Enter your details to generate your event token."
                : "Step 1: find the event. Step 2: enter donor details and generate the token."}
            </p>
          </div>

          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, setFieldValue, setFieldTouched, isSubmitting }) => (
              <Form className="space-y-6 p-6">
                {(() => {
                  const selectedEventors = [
                    selectedEventDetails?.eventor_1,
                    selectedEventDetails?.eventor_2,
                  ]
                    .filter(Boolean)
                    .join(" - ");
                  const selectedEventTitle =
                    selectedEventDetails?.customer_name ||
                    selectedEventDetails?.event_owner_name ||
                    selectedEventDetails?.event_type ||
                    (values.event_id ? `Event #${values.event_id}` : "Selected event");
                  const isProfileLocked =
                    isMyselfChecked ||
                    (!!existingCustomerMobile &&
                      existingCustomerMobile === values.mobile);

                  return (
                    <>
                      {isQuickTokenPath && isFetchingEventInfo ? (
                        <div className="flex min-h-[460px] flex-col items-center justify-center py-20 text-center animate-[fadeIn_0.25s_ease]">
                          <svg className="w-12 h-12 text-amber-600 animate-spin mb-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <p className="text-sm font-bold text-slate-700">Loading Event Details...</p>
                          <p className="mt-1 text-xs text-slate-500 font-medium">Please wait while we fetch the event information.</p>
                        </div>
                      ) : null}

                      {isQuickTokenPath && !isFetchingEventInfo && !selectedEventDetails ? (
                        <div className="flex min-h-[460px] items-center justify-center p-6">
                          <div className="w-full max-w-md bg-white border border-slate-100 rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-[fadeIn_0.25s_ease]">
                            <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-amber-100/60 shadow-sm animate-pulse">
                              <FaQrcode className="text-amber-600 text-3xl" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 mb-2">QR Code Scan Required</h3>
                            <p className="text-slate-500 text-xs font-medium leading-relaxed mb-6">
                              This page is only accessible by scanning the official event QR code at the entrance.
                              Please scan the QR code to load the correct event and proceed.
                            </p>
                          </div>
                        </div>
                      ) : null}

                      {!isQuickTokenPath && tokenStep === "city" ? (
                        <div className="flex min-h-[460px] items-center justify-center">
                          <div className="w-full max-w-xl animate-[fadeIn_0.25s_ease]">
                            <p className="mb-3 text-center text-[10px] font-bold uppercase tracking-widest text-amber-600">
                              Step 1
                            </p>
                            <label className="mb-3 block text-center text-base font-bold text-slate-800">
                              Select City
                            </label>
                            <CitySearchSelect
                              value={selectedCity}
                              onChange={handleCityFilterChange}
                              onBlur={() => { }}
                              noAutoTranslate
                              autoFocus
                              disabled={locationsLoading}
                              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 text-xs font-bold text-slate-700 placeholder:text-slate-400 shadow-sm outline-none transition focus:bg-white focus:border-amber-600 focus:ring-2 focus:ring-amber-500/20"
                              placeholder={
                                locationsLoading
                                  ? "Loading cities..."
                                  : "Type city name in English..."
                              }
                            />
                          </div>
                        </div>
                      ) : null}

                      {!isQuickTokenPath && tokenStep === "taluk" ? (
                        <div className="flex min-h-[460px] items-center justify-center">
                          <div className="w-full max-w-xl animate-[slideInRight_0.28s_ease]">
                            <button
                              type="button"
                              onClick={() => {
                                setTokenStep("city");
                                setIsLocationConfirmed(false);
                              }}
                              className="mb-6 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 shadow-sm"
                            >
                              Back
                            </button>
                            <p className="mb-3 text-center text-[10px] font-bold uppercase tracking-widest text-amber-600">
                              Step 2
                            </p>
                            <label className="mb-3 block text-center text-base font-bold text-slate-800">
                              Select Taluk / Area
                            </label>
                            <TalukSearchSelect
                                options={talukOptions}
                                value={selectedTaluk}
                                onChange={(taluk) => {
                                  setSelectedTaluk(taluk);
                                  if (!taluk) return;
                                  setIsLocationConfirmed(true);
                                  setShowAllEventResults(false);
                                  setTokenStep("event");
                                }}
                                onBlur={() => { }}
                                autoFocus
                                disabled={locationsLoading}
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 text-xs font-bold text-slate-700 shadow-sm outline-none transition focus:bg-white focus:border-amber-600 focus:ring-2 focus:ring-amber-500/20"
                                placeholder={
                                  locationsLoading
                                    ? "Loading areas..."
                                    : "Search taluk / area in English or Tamil..."
                                }
                              />
                          </div>
                        </div>
                      ) : null}

                      {!isQuickTokenPath && tokenStep === "event" ? (
                        <div className="animate-[slideInRight_0.28s_ease]">
                          <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-slate-100 bg-slate-50/50 px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
                                Step 2
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-800">
                                Select Event
                              </p>
                              <p className="text-xs font-medium text-slate-500 mt-0.5">
                                {[selectedCity, selectedTaluk].filter(Boolean).join(" - ")}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setTokenStep("city");
                                setIsLocationConfirmed(false);
                                setSelectedTaluk("");
                              }}
                              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 shadow-sm"
                            >
                              Back
                            </button>
                          </div>

                          <div className="mb-4 grid gap-3 md:grid-cols-[1fr_180px]">
                            <div className="relative">
                              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                              <input
                                type="text"
                                value={eventSearchQuery}
                                onChange={(event) => setEventSearchQuery(event.target.value)}
                                placeholder="Search by event, organisation, or eventor..."
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-11 pr-4 text-xs font-bold text-slate-700 shadow-sm outline-none transition focus:bg-white focus:border-amber-600 focus:ring-2 focus:ring-amber-500/20"
                              />
                            </div>
                            <input
                              type="date"
                              value={eventDateFilter}
                              min={todayKey}
                              onChange={(event) => {
                                setEventDateFilter(event.target.value);
                                setShowAllEventResults(false);
                              }}
                              className={fieldClassName}
                            />
                          </div>

                          <div className="mb-4 flex flex-wrap items-center gap-2">
                            {[
                              { id: "today", label: `Today's Events (${todayEvents.length})` },
                              { id: "all", label: `All Events (${filteredEventList.length})` },
                            ].map((tab) => (
                              <button
                                key={tab.id}
                                type="button"
                                onClick={() => {
                                  setEventListTab(tab.id);
                                  setShowAllEventResults(false);
                                }}
                                className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${activeEventListTab === tab.id
                                    ? "bg-amber-600 text-white shadow-sm"
                                    : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                                  }`}
                              >
                                {tab.label}
                              </button>
                            ))}
                            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-500">
                              Showing {visibleEventList.length} of {tabEventList.length}
                            </span>
                            {shouldFallbackToAllEvents ? (
                              <span className="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-700 ring-1 ring-amber-100/50">
                                No today's events, showing all
                              </span>
                            ) : null}
                          </div>

                          <div className="space-y-3">
                            {visibleEventList.map((eventItem) => {
                              const locationText = resolveEventLocation(eventItem);
                              const eventors = [eventItem?.eventor_1, eventItem?.eventor_2]
                                .filter(Boolean)
                                .join(" - ");

                              return (
                                <button
                                  key={eventItem.event_id}
                                  type="button"
                                  onClick={() => handleSelectEvent(eventItem, setFieldValue)}
                                  className="w-full rounded-2xl border border-slate-100 bg-white px-5 py-4 text-left transition hover:border-slate-300 hover:shadow-[0_4px_16px_rgba(0,0,0,0.03)]"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-sm font-bold text-slate-800">
                                        {eventItem.event_type || "Event"}
                                      </p>
                                      <p className="mt-1 text-xs font-medium text-slate-500">
                                        {eventItem.organisation_name || "Unknown organisation"}
                                      </p>
                                    </div>
                                    <span className="shrink-0 rounded-full bg-amber-50 border border-amber-200/50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
                                      Event #{eventItem.event_id}
                                    </span>
                                  </div>

                                  <div className="mt-3 grid gap-2 md:grid-cols-3">
                                    <div className="flex items-start gap-2 text-xs text-slate-600">
                                      <FaCalendarAlt className="mt-0.5 shrink-0" />
                                      <span>{formatCustomDateWithTime(eventItem.event_date)}</span>
                                    </div>
                                    <div className="flex items-start gap-2 text-xs text-slate-600">
                                      <FaMapMarkerAlt className="mt-0.5 shrink-0" />
                                      <span className="line-clamp-2">{locationText}</span>
                                    </div>
                                    <div className="flex items-start gap-2 text-xs text-slate-600">
                                      <FaUsers className="mt-0.5 shrink-0" />
                                      <span className="line-clamp-2">{eventors || "No eventors added"}</span>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}

                            {isSearching ? (
                              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-xs font-medium text-slate-500">
                                Loading events...
                              </div>
                            ) : null}
                            {!isSearching && tabEventList.length === 0 ? (
                              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-xs font-medium text-slate-500">
                                No events found for the selected filters.
                              </div>
                            ) : null}
                            {tabEventList.length > 8 ? (
                              <div className="flex justify-center pt-2">
                                <button
                                  type="button"
                                  onClick={() => setShowAllEventResults((current) => !current)}
                                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 shadow-sm"
                                >
                                  {showAllEventResults
                                    ? "Show fewer results"
                                    : `Show more results (${tabEventList.length - visibleEventList.length} more)`}
                                </button>
                              </div>
                            ) : null}
                          </div>

                          <details className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                            <summary className="cursor-pointer font-semibold text-slate-700">
                              Advanced fallback dropdown
                            </summary>
                            <div className="mt-3">
                              <Select
                                ref={selectRef}
                                value={null}
                                onChange={(value) => {
                                  if (value && value.value) {
                                    handleSelectEvent(value.rawData, setFieldValue);
                                  }
                                }}
                                options={mergedEventInfoList.map((item) => ({
                                  value: String(item.event_id),
                                  label: `${item.organisation_name || "Organisation"} - ${item.event_type || "Event"} - ${formatCustomDateWithTime(item.event_date)}`,
                                  rawData: item,
                                }))}
                                className="basic-single"
                                classNamePrefix="select"
                                styles={selectStyles}
                                menuPortalTarget={
                                  typeof document !== "undefined" ? document.body : undefined
                                }
                                menuPosition="fixed"
                                menuPlacement="auto"
                                maxMenuHeight={260}
                                isSearchable
                                placeholder="Search and select an event..."
                                isLoading={isSearching}
                                noOptionsMessage={() => "No events found"}
                              />
                            </div>
                          </details>
                          <ErrorMessage
                            name="event_id"
                            component="div"
                            className="mt-1 text-sm text-red-500"
                          />
                        </div>
                      ) : null}

                      {(isQuickTokenPath || tokenStep === "token") && values.event_id && selectedEventDetails ? (
                        <>
                          <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-5 shadow-sm">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">
                                  Selected Event
                                </p>
                                <p className="mt-1 text-sm font-bold leading-snug text-slate-800">
                                  {selectedEventTitle}
                                </p>
                                {selectedEventors && (
                                  <p className="mt-0.5 text-xs font-semibold text-slate-500">
                                    {selectedEventors}
                                  </p>
                                )}
                                <div className="mt-3 grid gap-2 text-xs font-medium text-slate-500 sm:grid-cols-2">
                                  <div className="flex items-start gap-2">
                                    <FaCalendarAlt className="mt-0.5 shrink-0 text-amber-600" />
                                    <span>
                                      {selectedEventDetails?.event_type || "Event"} -{" "}
                                      {selectedEventDetails?.event_date
                                        ? formatCustomDateWithTime(selectedEventDetails.event_date)
                                        : `Event #${values.event_id}`}
                                    </span>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <FaUsers className="mt-0.5 shrink-0 text-amber-600" />
                                    <span>
                                      {selectedEventDetails?.organisation_name || "Organisation"} - Event #
                                      {selectedEventDetails?.event_id || values.event_id}
                                    </span>
                                  </div>
                                  <div className="flex items-start gap-2 sm:col-span-2">
                                    <FaMapMarkerAlt className="mt-0.5 shrink-0 text-amber-600" />
                                    <span className="line-clamp-2">
                                      {eventLocation}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {!isQuickTokenPath && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFieldValue("event_id", "");
                                    setSelectedEventDetails(null);
                                    setIsMyselfChecked(false);
                                    setTokenStep("event");
                                  }}
                                  className="shrink-0 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 shadow-sm"
                                >
                                  Select Different Event
                                </button>
                              )}
                            </div>
                          </div>

                             {/* Token for Myself Checkbox */}
                          {userLoggedIn && (
                            <div className={`flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/50 px-4 py-3 ${!values.event_id ? "opacity-50 pointer-events-none" : ""}`}>
                              <input
                                id="tokenForMyself"
                                type="checkbox"
                                checked={isMyselfChecked}
                                disabled={!values.event_id || isFetchingCustomerInfo}
                                onChange={(e) => handleMyselfCheckbox(e.target.checked, setFieldValue)}
                                className="h-4 w-4 accent-amber-600 cursor-pointer disabled:cursor-not-allowed"
                              />
                              <label
                                htmlFor="tokenForMyself"
                                className={`text-xs font-bold select-none ${values.event_id ? "text-slate-700 cursor-pointer" : "text-slate-400 cursor-not-allowed"}`}
                              >
                                Token for myself
                                {isFetchingCustomerInfo && (
                                  <span className="ml-2 text-xs text-amber-700 animate-pulse">Fetching your info…</span>
                                )}
                              </label>
                            </div>
                          )}

                          {/* Form Fields with visual overlay (non-interactive) */}
                          <div className="relative rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                            <div className="mb-4">
                              <p className="text-xs font-bold text-slate-700">
                                Step 2: Fill Token Details
                              </p>
                              <p className="text-xs text-slate-500 font-medium">
                                Once the event is selected, the token form unlocks below.
                              </p>
                              {existingCustomerMobile ? (
                                <p className="mt-1 text-[10px] font-semibold text-amber-700 leading-relaxed bg-amber-50 border border-amber-100/50 px-3 py-2 rounded-lg">
                                  Existing customer details were filled from this mobile number. Change the mobile number to edit details.
                                </p>
                              ) : null}
                            </div>
                            {!values.event_id && (
                              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/70 pointer-events-none backdrop-blur-[1px]">
                                <p className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 shadow-sm">
                                  Please select an event first
                                </p>
                              </div>
                            )}
                            <div className={!values.event_id ? "opacity-50" : ""}>
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">

                                {/* Date & Time — top, full width, display only */}
                                {!isMyselfChecked && (
                                  <div className="md:col-span-2">
                                    {/* Hidden field keeps datetime in Formik state for validation & submit */}
                                    <Field type="hidden" name="datetime" />
                                    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                        <rect x="3" y="4" width="18" height="18" rx="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                      </svg>
                                      <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Entry Date & Time</p>
                                        <p className="text-sm font-bold text-slate-800">
                                          {new Date().toLocaleString("en-IN", {
                                            timeZone: "Asia/Kolkata",
                                            weekday: "short",
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: true,
                                          })}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Mobile (left) + Email (right) — same aligned row */}
                                {!isMyselfChecked && (
                                  <div className="md:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {/* Mobile */}
                                    <div className="flex flex-col">
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">
                                        Mobile / Permanent Token Number
                                        <span className="ml-1 text-xs font-normal text-slate-400">Auto-fills when the mobile number already exists</span>
                                      </label>
                                      <Field name="mobile">
                                        {({ field }) => (
                                          <input
                                            {...field}
                                            id="mobile"
                                            type="tel"
                                            inputMode="numeric"
                                            className={isMyselfChecked ? disabledFieldClassName : fieldClassName}
                                            placeholder="10-digit mobile number"
                                            disabled={!values.event_id || isMyselfChecked}
                                            onChange={(event) =>
                                              handleMobileChange(event, setFieldValue)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key !== "Enter") return;
                                              e.preventDefault();
                                              villageRef.current?.focus();
                                            }}
                                          />
                                        )}
                                      </Field>
                                      {isFetchingCustomerInfo && !isMyselfChecked ? (
                                        <p className="mt-1 text-[10px] font-semibold text-amber-700 animate-pulse">
                                          Checking mobile number...
                                        </p>
                                      ) : null}
                                      <ErrorMessage
                                        name="mobile"
                                        component="div"
                                        className="mt-1 text-sm text-red-400"
                                      />
                                    </div>

                                    {/* Email (Optional) */}
                                    <div className="flex flex-col">
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">
                                        Email (Optional)
                                      </label>
                                      <Field
                                        id="email"
                                        name="email"
                                        type="email"
                                        className={isProfileLocked ? disabledFieldClassName : fieldClassName}
                                        placeholder="Email address"
                                        disabled={!values.event_id || isProfileLocked}
                                      />
                                      <ErrorMessage
                                        name="email"
                                        component="div"
                                        className="mt-1 text-sm text-red-400"
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* LEFT COLUMN: Village, City, Name, Occupation */}
                                {!isMyselfChecked && (
                                  <div className="flex flex-col gap-3">

                                    {/* Village */}
                                    <div>
                                      <div className="mb-1 flex items-center justify-between gap-1.5 flex-wrap">
                                        <label className="text-sm font-semibold text-slate-800">
                                          Village
                                          <span className="ml-1 text-xs font-normal text-slate-400">(Village, Area or City — at least one required)</span>
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => setVillageSuggestionEnabled((prev) => !prev)}
                                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold border transition-all select-none shadow-sm cursor-pointer ${
                                            villageSuggestionEnabled
                                              ? "bg-indigo-50 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                                              : "bg-slate-100 border-slate-300 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                                          }`}
                                          title={
                                            villageSuggestionEnabled
                                              ? "DB suggestions are ON — click to turn OFF"
                                              : "DB suggestions are OFF — click to turn ON"
                                          }
                                        >
                                          <span
                                            className={`inline-block w-2 h-2 rounded-full transition-colors ${
                                              villageSuggestionEnabled ? "bg-indigo-600 animate-pulse" : "bg-slate-400"
                                            }`}
                                          />
                                          <span>DB Suggestion:</span>
                                          <span
                                            className={`uppercase font-bold text-[10px] px-1 py-0.5 rounded ${
                                              villageSuggestionEnabled
                                                ? "bg-indigo-600 text-white"
                                                : "bg-slate-200 text-slate-600"
                                            }`}
                                          >
                                            {villageSuggestionEnabled ? "ON" : "OFF"}
                                          </span>
                                        </button>
                                      </div>
                                      <VillageAutocomplete
                                        name="village"
                                        enabled={villageSuggestionEnabled}
                                        placeholder="Village name"
                                        disabled={!values.event_id || isProfileLocked}
                                        className={isProfileLocked ? disabledFieldClassName : fieldClassName}
                                        inputRef={villageRef}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            e.preventDefault();
                                            areaRef.current?.focus();
                                          }
                                        }}
                                      />
                                      <ErrorMessage
                                        name="village"
                                        component="div"
                                        className="mt-1 text-sm text-red-400"
                                      />
                                    </div>

                                    {/* Area — same optional location field as moi entry */}
                                    <div>
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">Area</label>
                                      <Field name="area">
                                        {({ field }) => (
                                          <input {...field} id="area" type="text" ref={areaRef}
                                            className={isProfileLocked ? disabledFieldClassName : fieldClassName}
                                            placeholder="Area (optional)" disabled={!values.event_id || isProfileLocked}
                                            onKeyDown={(e) => {
                                              if (e.key !== "Enter") return;
                                              e.preventDefault();
                                              cityRef.current?.focus();
                                            }} />
                                        )}
                                      </Field>
                                      <ErrorMessage name="area" component="div" className="mt-1 text-sm text-red-500" />
                                    </div>

                                    {/* City */}
                                    <div>
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">
                                        City
                                        <span className="ml-1 text-xs font-normal text-slate-400">(Village, Area or City — at least one required)</span>
                                      </label>
                                      <CitySearchSelect
                                        value={values.city}
                                        onChange={(city) => setFieldValue("city", city)}
                                        onBlur={() => setFieldTouched("city", true)}
                                        onNext={() => initialRef.current?.focus()}
                                        inputRef={cityRef}
                                        className={isProfileLocked ? disabledFieldClassName : fieldClassName}
                                        placeholder="City name"
                                        disabled={!values.event_id || isProfileLocked}
                                      />
                                      <ErrorMessage
                                        name="city"
                                        component="div"
                                        className="mt-1 text-sm text-red-400"
                                      />
                                    </div>

                                    {/* Initial — stored with the name, as in moi entry */}
                                    <div>
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">Initial</label>
                                      <Field name="initial">
                                        {({ field }) => (
                                          <input {...field} id="initial" type="text" ref={initialRef}
                                            className={`${isProfileLocked ? disabledFieldClassName : fieldClassName} no-translate uppercase`}
                                            placeholder="Initial (optional)" disabled={!values.event_id || isProfileLocked}
                                            onChange={(e) => setFieldValue("initial", e.target.value.toUpperCase())}
                                            onKeyDown={(e) => {
                                              if (e.key !== "Enter") return;
                                              e.preventDefault();
                                              nameRef.current?.focus();
                                            }} />
                                        )}
                                      </Field>
                                      <ErrorMessage name="initial" component="div" className="mt-1 text-sm text-red-500" />
                                    </div>

                                    {/* Name */}
                                    <div>
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">
                                        Name <span className="text-red-500">*</span>
                                      </label>
                                      <Field name="name">
                                        {({ field }) => (
                                          <input
                                            {...field}
                                            id="name"
                                            type="text"
                                            ref={nameRef}
                                            className={isProfileLocked ? disabledFieldClassName : fieldClassName}
                                            placeholder="Full name"
                                            disabled={!values.event_id || isProfileLocked}
                                            onKeyDown={(e) => {
                                              if (e.key !== "Enter") return;
                                              e.preventDefault();
                                              occupationRef.current?.focus();
                                            }}
                                          />
                                        )}
                                      </Field>
                                      <ErrorMessage
                                        name="name"
                                        component="div"
                                        className="mt-1 text-sm text-red-500"
                                      />
                                    </div>

                                    {/* Occupation */}
                                    <div>
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">
                                        Occupation
                                      </label>
                                      <Field name="occupation">
                                        {({ field }) => (
                                          <input
                                            {...field}
                                            id="occupation"
                                            type="text"
                                            ref={occupationRef}
                                            className={isProfileLocked ? disabledFieldClassName : fieldClassName}
                                            placeholder="Optional"
                                            disabled={!values.event_id || isProfileLocked}
                                            onKeyDown={(e) => {
                                              if (e.key !== "Enter") return;
                                              e.preventDefault();
                                              const nextRef = (values.assetType === "things" || values.assetType === "ornaments") ? thingnameRef : amountRef;
                                              nextRef.current?.focus();
                                            }}
                                          />
                                        )}
                                      </Field>
                                      <ErrorMessage
                                        name="occupation"
                                        component="div"
                                        className="mt-1 text-sm text-red-500"
                                      />
                                    </div>

                                  </div>
                                )}

                                {/* RIGHT COLUMN: Father/Husband Name, State, Asset Type, Amount/Thingname, Date & Time, Email */}
                                <div className="flex flex-col gap-3">

                                  {/* Father / Husband Name */}
                                  {!isMyselfChecked && (
                                    <div>
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">
                                        Father / Husband Name
                                      </label>
                                      <Field
                                        id="familyName"
                                        name="familyName"
                                        type="text"
                                        className={isProfileLocked ? disabledFieldClassName : fieldClassName}
                                        placeholder="Optional"
                                        disabled={!values.event_id || isProfileLocked}
                                      />
                                      <ErrorMessage
                                        name="familyName"
                                        component="div"
                                        className="mt-1 text-sm text-red-400"
                                      />
                                    </div>
                                  )}

                                  {/* State */}
                                  {!isMyselfChecked && (
                                    <div>
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">
                                        State <span className="text-red-500">*</span>
                                      </label>
                                      <Field
                                        id="state"
                                        name="state"
                                        type="text"
                                        className={isProfileLocked ? disabledFieldClassName : fieldClassName}
                                        disabled={!values.event_id || isProfileLocked}
                                      />
                                      <ErrorMessage
                                        name="state"
                                        component="div"
                                        className="mt-1 text-sm text-red-400"
                                      />
                                    </div>
                                  )}

                                  {/* Asset Type */}
                                  <div>
                                    <label className="mb-1 block text-sm font-semibold text-slate-800">
                                      Asset Type <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      as="select"
                                      id="assetType"
                                      name="assetType"
                                      className={fieldClassName}
                                      ref={assetTypeRef}
                                      disabled={!values.event_id}
                                    >
                                      <option value="cash">💵 Cash</option>
                                      <option value="online">📲 Online Payment</option>
                                      <option value="ornaments">💍 Ornaments</option>
                                      <option value="things">🎁 Things</option>
                                    </Field>
                                    <ErrorMessage
                                      name="assetType"
                                      component="div"
                                      className="mt-1 text-sm text-red-500"
                                    />
                                  </div>

                                  {/* Amount or Thing Name */}
                                  {(values.assetType === "things" || values.assetType === "ornaments") ? (
                                    <div>
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">
                                        What you received? <span className="text-red-500">*</span>
                                      </label>
                                      <Field name="thingname">
                                        {({ field }) => (
                                          <input
                                            {...field}
                                            id="thingname"
                                            type="text"
                                            ref={thingnameRef}
                                            className={fieldClassName}
                                            placeholder="e.g., Gold ring, 1 kg rice"
                                            disabled={!values.event_id}
                                            onKeyDown={(e) => {
                                              if (e.key !== "Enter") return;
                                              e.preventDefault();
                                              submitRef.current?.click();
                                            }}
                                          />
                                        )}
                                      </Field>
                                      <ErrorMessage
                                        name="thingname"
                                        component="div"
                                        className="mt-1 text-sm text-red-500"
                                      />
                                    </div>
                                  ) : (
                                    <div>
                                      <label className="mb-1 block text-sm font-semibold text-slate-800">
                                        Amount <span className="text-red-500">*</span>
                                      </label>
                                      <Field name="amount">
                                        {({ field }) => (
                                          <input
                                            {...field}
                                            id="amount"
                                            type="number"
                                            step="0.01"
                                            ref={amountRef}
                                            className={fieldClassName}
                                            placeholder="Enter amount"
                                            disabled={!values.event_id}
                                            onKeyDown={(e) => {
                                              if (e.key !== "Enter") return;
                                              e.preventDefault();
                                              submitRef.current?.click();
                                            }}
                                          />
                                        )}
                                      </Field>
                                      <ErrorMessage
                                        name="amount"
                                        component="div"
                                        className="mt-1 text-sm text-red-500"
                                      />
                                    </div>
                                  )}

                                </div>

                              </div>
                            </div>
                          </div>

                          {/* Submit Button */}
                          <div className="pt-4">
                            <button
                              ref={submitRef}
                              type="submit"
                              disabled={
                                !values.event_id ||
                                isSubmitting ||
                                isFetchingEventInfo ||
                                isFetchingCustomerInfo
                              }
                              className={`flex w-full justify-center rounded-xl border px-4 py-3 text-sm font-bold text-white shadow-lg transition ${!values.event_id ||
                                  isSubmitting ||
                                  isFetchingEventInfo ||
                                  isFetchingCustomerInfo
                                  ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                                  : "border-amber-500/20 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:ring-offset-0"
                                }`}
                            >
                              {isSubmitting ? (
                                <>
                                  <svg
                                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Processing...
                                </>
                              ) : (
                                "Get Token"
                              )}
                            </button>
                          </div>
                        </>
                      ) : null}
                    </>
                  );
                })()}
              </Form>
            )}
          </Formik>
        </div>

        {/* Event Invitation Preview Sidebar */}
        {selectedEventDetails && (isQuickTokenPath || tokenStep === "token") ? (
          <div className="w-full lg:w-96">
            <div className="sticky top-8 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-100/60 backdrop-blur-sm">
              <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-slate-100/50 px-6 py-5">
                <h3 className="text-lg font-black text-slate-950">Event Reference</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-widest">Event Type</p>
                    <p className="text-sm font-black text-slate-950">{selectedEventDetails.event_type}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-widest">Event Date</p>
                    <p className="text-sm font-black text-slate-950">{formatCustomDateWithTime(selectedEventDetails.event_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-widest">Location</p>
                    <p className="text-sm font-black text-slate-950 break-words">{eventLocation}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-widest">Customer</p>
                    <p className="text-sm font-black text-slate-950">{selectedEventDetails?.customer_name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-widest">Eventors</p>
                    <p className="text-sm font-black text-slate-950">
                      {[selectedEventDetails?.eventor_1, selectedEventDetails?.eventor_2]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </p>
                  </div>

                  {selectedEventDetails?.accept_online_payments &&
                    (accountDetails || upiId || rawPaymentDetails) && (
                      <div className="rounded-2xl border border-amber-100 bg-amber-50/50 p-4">
                        <p className="text-xs font-bold text-amber-800 uppercase tracking-widest">
                          Payment Details
                        </p>
                        <p className="mt-1 text-xs text-amber-900/70">
                          Use these details when sending online payment for this
                          event.
                        </p>

                        {accountDetails && (
                          <div className="mt-3 rounded-xl border border-amber-100/60 bg-white p-3">
                            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                              Account Details
                            </p>
                            <p className="mt-2 text-sm font-semibold text-slate-950">
                              Holder: {accountDetails.holder_name || "—"}
                            </p>
                            <p className="mt-1 text-sm text-slate-600">
                              Account Number:{" "}
                              {accountDetails.account_number || "—"}
                            </p>
                            <p className="mt-1 text-sm text-slate-600">
                              IFSC Code: {accountDetails.ifsc_code || "—"}
                            </p>
                          </div>
                        )}

                        {upiId && (
                          <div className="mt-3 rounded-xl border border-amber-100/60 bg-white p-3">
                            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                              UPI Details
                            </p>
                            <p className="mt-2 text-sm font-semibold text-slate-950">
                              UPI ID: {upiId}
                            </p>
                          </div>
                        )}

                        {rawPaymentDetails && (
                          <div className="mt-3 rounded-xl border border-amber-100/60 bg-white p-3">
                            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                              Payment Details
                            </p>
                            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                              {rawPaymentDetails}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-2">Invitation Card</p>
                    {invitationPreviewUrl && isInvitationAvailable ? (
                      <div className="flex max-h-[420px] items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60 shadow-sm">
                        <img
                          src={invitationPreviewUrl}
                          alt="Event Invitation"
                          className="max-h-[420px] w-full object-contain cursor-zoom-in hover:opacity-95 transition"
                          onError={() => setIsInvitationAvailable(false)}
                          onClick={() => window.open(invitationPreviewUrl, "_blank")}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 bg-slate-50/70 rounded-2xl border-2 border-dashed border-slate-200">
                        <span className="text-4xl mb-2">📄</span>
                        <p className="text-xs text-slate-500 font-medium">No invitation card available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CreateMoiToken;
